import useControlled from '@material-ui/core/utils/useControlled';
import PropTypes from 'prop-types';
import T from "ramda/es/T";
import always from "ramda/es/always";
import compose from "ramda/es/compose";
import concat from "ramda/es/concat";
import cond from "ramda/es/cond";
import equals from "ramda/es/equals";
import find from "ramda/es/find";
import isNil from "ramda/es/isNil";
import map from "ramda/es/map";
import prop from "ramda/es/prop";
import propEq from "ramda/es/propEq";
import props from "ramda/es/props";
import reduce from "ramda/es/reduce";
import when from "ramda/es/when";
import cx from "ramda-extension/es/cx";
import defaultToEmptyObject from "ramda-extension/es/defaultToEmptyObject";
import rejectNil from "ramda-extension/es/rejectNil";
import React, { createElement, useCallback, useMemo, useRef } from 'react';
import { resolveValidation } from '../../../molecules/Validation';
import Button from '../../Button';
import Upload from '../../SvgIcon/Icons/Upload';
import { validationDisplayTypePropType } from '../../../molecules/Validation/propTypes';
import FileRecord from '../FileRecord';
const filesProp = prop('files');
const findByIndex = (index, errors)=>compose(prop('errorMessage'), defaultToEmptyObject, find(propEq(index, 'index')))(errors);
const rejectAndRevoke = (name, files)=>reduce((acc, file)=>{
        if (file.name === name) {
            URL.revokeObjectURL(file.blob);
            return acc;
        }
        return [
            ...acc,
            file
        ];
    }, [], files);
const uniqueProps = props([
    'name',
    'size',
    'type'
]);
const isAlreadyAdded = (value)=>(newFile)=>find((oldFile)=>equals(uniqueProps(newFile), uniqueProps(oldFile)), value);
const FileInput = ({ id, className, accept, disabled = false, multiple = false, customButton, hideValidationText, validationClassName, validationText, fileRecord = FileRecord, handleFileChange, errMeta = [], validationDisplayType, tooltipComponent, value: valueProp })=>{
    const [value, setValue] = useControlled({
        controlled: valueProp,
        default: [],
        name: 'FileInput'
    });
    const fileInputRef = useRef(null);
    const inputId = useMemo(()=>id || crypto.randomUUID(), [
        id
    ]);
    const validationElement = resolveValidation({
        hideValidationText,
        validationText,
        validationClassName,
        validationDisplayType,
        tooltipComponent
    });
    const handleRemoveFile = useCallback((name)=>{
        const newFiles = rejectAndRevoke(name, value);
        setValue(newFiles);
        handleFileChange?.(null, newFiles);
    }, [
        value,
        handleFileChange,
        setValue
    ]);
    const handleOnChange = useCallback((e)=>{
        if (disabled || e.target.value === '' && e.target.files.length === 0) {
            // IE11 will trigger on change when setting value. Ignore this change.
            return;
        }
        const checkFileValidity = cond([
            [
                isNil,
                always(null)
            ],
            [
                isAlreadyAdded(value),
                always(null)
            ],
            [
                T,
                (f)=>({
                        name: f.name,
                        size: f.size,
                        type: f.type,
                        blob: URL.createObjectURL(f),
                        file: f
                    })
            ]
        ]);
        const newFiles = compose(rejectNil, when(always(multiple), concat(value)), map(checkFileValidity), filesProp)(e.target);
        setValue(newFiles);
        handleFileChange?.(e, newFiles);
        if (fileInputRef.current) {
            // NOTE: this will reset DOM value of input. So it allows to select same file twice.
            // Typical usecase is: select file, remove it, select same file.
            fileInputRef.current.value = '';
        }
    }, [
        handleFileChange,
        disabled,
        value,
        setValue,
        multiple
    ]);
    return /*#__PURE__*/ React.createElement("div", {
        className: cx('FileInput', className)
    }, /*#__PURE__*/ React.createElement("input", {
        className: "FileInput-input",
        type: "file",
        ref: fileInputRef,
        accept: accept,
        onChange: handleOnChange,
        disabled: disabled,
        multiple: multiple,
        id: inputId
    }), /*#__PURE__*/ React.createElement("label", {
        className: "FileInput-control",
        htmlFor: inputId
    }, customButton ? customButton : /*#__PURE__*/ React.createElement(Button, {
        className: "FileInput-button",
        containerElement: "span",
        icon: /*#__PURE__*/ React.createElement(Upload, null),
        primary: true,
        disabled: disabled,
        label: ""
    })), validationElement, rejectNil(value || []).map((file, index)=>/*#__PURE__*/ createElement(fileRecord, {
            ...file,
            error: findByIndex(index, errMeta),
            onRemove: handleRemoveFile,
            key: file.name
        })));
};
FileInput.propTypes = {
    /**
	 * html accept attribute, see https://www.w3schools.com/TAGs/att_input_accept.asp
	 */ accept: PropTypes.string,
    /**
	 * Custom `className` for the root.
	 */ className: PropTypes.string,
    /**
	 * Custom Button component
	 */ customButton: PropTypes.node,
    /**
	 * If true, the select field will be disabled.
	 */ disabled: PropTypes.bool,
    /**
	 * Metadata from validations with info about specific errors for file
	 */ errMeta: PropTypes.arrayOf(PropTypes.shape({
        errorMessage: PropTypes.string,
        index: PropTypes.number
    })),
    /**
	 * File record element
	 */ fileRecord: PropTypes.elementType,
    /**
	 * Callback function fired when file is selected
	 *
	 * @param {object} event event targeting the element.
	 * @param {File} file object. https://developer.mozilla.org/cs/docs/Web/API/File
	 * @return {undefined} Nothin'
	 */ handleFileChange: PropTypes.func,
    /**
	 * Whether to render validation component.
	 */ hideValidationText: PropTypes.bool,
    /**
	 * Id of the form element . If not specified, one will be generated automatically to connect
	 * input with label using the `for` attribute.
	 */ id: PropTypes.string,
    /**
	 * If true, more files can be selected.
	 */ multiple: PropTypes.bool,
    /**
	 * component for tooltip validation
	 */ tooltipComponent: PropTypes.any,
    /**
	 * Set class for validation component.
	 */ validationClassName: PropTypes.string,
    /**
	 * Where to display validation
	 */ validationDisplayType: validationDisplayTypePropType,
    /**
	 * Text that will be shown when withValidity decorator decides to show validation text.
	 */ validationText: PropTypes.node,
    /**
	 * Value for controlled component
	 */ value: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
        size: PropTypes.number,
        blob: PropTypes.string,
        error: PropTypes.string
    }))
};
export default FileInput;
